import React, { useEffect, useRef, useState } from "react"
import styles from "./reportDataLead.module.css"
import { MdOutlineFileDownload } from "react-icons/md"
import { toast } from "react-toastify"
import Button from "../../components/Form/Button"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import FormGroup from "../../components/Form/FormGroup"
import Input from "../../components/Form/Input"
import DatePicker from "../../components/Form/DatePicker"
import Select from "../../components/Form/Select"
import BreakForm from "../../components/Form/BreakForm"
import { useAuth } from "../../contexts/authContext"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { useData } from "../../contexts/dataContext"
import { getStages } from "../../schemas/Stage"
import { getSavedLeadFilters } from "../../schemas/SavedLeadFilter"
import { resolveRelativeLeadFilterDates } from "../../utils/leadFilters"
import AdditionalFieldFilters from "../../components/LeadFilters/AdditionalFieldFilters"
import ActiveFilterChips, { buildLeadFilterChips } from "../../components/LeadFilters/ActiveFilterChips"
import CollectionFilter from "../../components/LeadFilters/CollectionFilter"
import SavedFilters from "../../components/LeadFilters/SavedFilters"
import UtmFilters from "../../components/LeadFilters/UtmFilters"
import { getUsersBySelectedTeams, getValidUserIdsByTeams } from "../../utils/utils"
import { interactionStatusOptions } from "../../utils/interactionFilters"

const ReportDataLead = () => {
    const [filters, setFilters] = useState({})
    const [stages, setStages] = useState([])
    const [loading, setLoading] = useState(false)
    const defaultFilterLoaded = useRef("")

    const { account_id } = useParams()
    const {
        funnels, teams, users, products, origins, campaigns, typeDate, status, flagsByoptions, additionalFields,
        metaAdsAdsName, metaAdsAdsetsName, metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
        loadCampaignsByOrigins,
    } = useData()
    const { permissions, user } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        loadCampaignsByOrigins(filters.origins)
    }, [filters.origins])

    useEffect(() => {
        let active = true

        const loadStages = async () => {
            setStages([])
            if (!filters.funnel_id) return
            const listStages = await getStages(filters.funnel_id)
            if (active) setStages((listStages || []).map(stage => ({ id: stage._id, value: stage.name })))
        }

        loadStages()
        return () => { active = false }
    }, [filters.funnel_id])

    useEffect(() => {
        const defaultFilterKey = `${account_id || ""}:${user?._id || ""}`
        if (!account_id || !user?._id || !teams?.length || !users?.length || defaultFilterLoaded.current === defaultFilterKey) return
        defaultFilterLoaded.current = defaultFilterKey

        getSavedLeadFilters("report-leads").then(savedFilters => {
            const defaultFilter = savedFilters.find(item => item.is_default)
            if (defaultFilter?.filters) {
                const resolvedFilters = resolveRelativeLeadFilterDates(defaultFilter.filters)
                setFilters({ ...resolvedFilters, users: getValidUserIdsByTeams(resolvedFilters.users, resolvedFilters.teams, users, teams) })
            }
        }).catch(() => { })
    }, [account_id, user?._id, teams, users])

    const handleChange = (name, value) => {
        setFilters(previousValue => {
            const operatorKey = ["users", "products", "flags"].includes(name) ? `${name}Operator` : undefined
            const resetPresenceOperator = operatorKey && value?.length > 0 && ["is_empty", "is_not_empty"].includes(previousValue[operatorKey])
            return {
                ...previousValue,
                [name]: value,
                ...(resetPresenceOperator ? { [operatorKey]: "any" } : {}),
                ...(name === "teams" ? { users: getValidUserIdsByTeams(previousValue.users, value, users, teams, [], previousValue.teams) } : {}),
            }
        })
    }

    const handleChangePeriod = (dateStart, dateEnd, datePreset = null) => {
        setFilters(previousValue => ({ ...previousValue, dateStart, dateEnd, datePreset }))
    }

    const handleChangeFunnel = funnel_id => {
        setFilters(previousValue => ({ ...previousValue, funnel_id, stages: [] }))
    }

    const handleSubmit = () => {
        if (!filters.funnel_id) return toast.error("O funil é obrigatório!")

        setLoading(true)
        const resolvedFilters = resolveRelativeLeadFilterDates(filters)
        setFilters(resolvedFilters)
        api.post("/reports/leads", resolvedFilters).then(response => {
            toast.success(response?.data?.message)
            navigate(`/${account_id}/reports/log`)
        }).catch(err => {
            toast.error(err?.response?.data?.message)
        }).finally(() => setLoading(false))
    }

    const activeFilterChips = buildLeadFilterChips({
        filters,
        options: {
            funnels, stages, products, origins, campaigns, flags: flagsByoptions, typeDate, status, teams, users,
            metaAdsAdsName, metaAdsAdsetsName, metaAdsCampaignsName, metaAdsFormsName, metaAdsPagesName,
        },
    })

    return (
        <div className={styles.content}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <MdOutlineFileDownload />
                        <div>
                            <strong>Exportar relatório de leads</strong>
                            <span>Use os mesmos critérios disponíveis no Kanban</span>
                        </div>
                    </div>
                    <NavLink to={`/${account_id}/reports/log`}>Histórico de exportações</NavLink>
                </div>

                <SavedFilters scope="report-leads" filters={filters} onLoad={value => {
                    const resolvedFilters = resolveRelativeLeadFilterDates(value)
                    setFilters({ ...resolvedFilters, users: getValidUserIdsByTeams(resolvedFilters.users, resolvedFilters.teams, users, teams) })
                }} />
                <ActiveFilterChips items={activeFilterChips} />

                <div className={styles.body}>
                    <BreakForm title="Filtros principais" showAction={false} borderColor="var(--border-subtle-color)" />
                    <FormGroup labelText="Funil">
                        <Select name="Funil" placeholder="Pesquise um funil" options={funnels || []} selected={filters.funnel_id || ""} onChange={handleChangeFunnel} multiple={false} />
                    </FormGroup>
                    <FormGroup labelText="Etapa">
                        <Select name="Etapas" placeholder={filters.funnel_id ? "Pesquise uma etapa" : "Selecione um funil primeiro"} options={stages} selected={filters.stages || []} onChange={value => handleChange("stages", value)} multiple={true} disabled={!filters.funnel_id} />
                    </FormGroup>
                    <FormGroup labelText="Busca por texto">
                        <Input type="search" placeholder="Buscar" value={filters.search || ""} onChange={event => handleChange("search", event.target.value)} />
                    </FormGroup>
                    <FormGroup labelText="Período">
                        <div className={styles.correlatedFilter}>
                            <div>
                                <span>Intervalo</span>
                                <DatePicker name="Período" dateStart={filters.dateStart || ""} dateEnd={filters.dateEnd || ""} onChange={(dateStart, dateEnd, _timeStart, _timeEnd, metadata) => handleChangePeriod(dateStart, dateEnd, metadata?.preset)} multiple={true} />
                            </div>
                            <div>
                                <span>Filtrar pela data de</span>
                                <Select name="Tipo de data" placeholder="Pesquise uma opção" options={typeDate || []} selected={filters.typeDate || ""} onChange={value => handleChange("typeDate", value)} multiple={false} />
                            </div>
                        </div>
                    </FormGroup>

                    <BreakForm title="Relacionamentos" showAction={false} borderColor="var(--border-subtle-color)" />
                    <CollectionFilter label="Produto" name="Produtos" placeholder="Pesquise um produto" options={products || []} selected={filters.products || []} operator={filters.productsOperator || "any"} onValuesChange={value => handleChange("products", value)} onOperatorChange={value => handleChange("productsOperator", value)} />
                    <FormGroup labelText="Origem">
                        <Select name="Origens" placeholder="Pesquise uma origem" options={origins || []} selected={filters.origins || []} onChange={value => handleChange("origins", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Campanha">
                        <Select name="Campanhas" placeholder="Pesquise uma campanha" options={campaigns || []} selected={filters.campaigns || []} onChange={value => handleChange("campaigns", value)} multiple={true} />
                    </FormGroup>
                    <CollectionFilter label="Etiqueta" name="Etiquetas" placeholder="Pesquise uma etiqueta" options={flagsByoptions || []} selected={filters.flags || []} operator={filters.flagsOperator || "any"} onValuesChange={value => handleChange("flags", value)} onOperatorChange={value => handleChange("flagsOperator", value)} />
                    <FormGroup labelText="Status">
                        <Select name="Status" placeholder="Pesquise um status" options={status || []} selected={filters.status || []} onChange={value => handleChange("status", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Interação">
                        <Select name="Interação" placeholder="Todos" options={interactionStatusOptions} selected={filters.interactionStatus || ""} onChange={value => handleChange("interactionStatus", value)} multiple={false} />
                    </FormGroup>
                    <FormGroup labelText="Equipe">
                        <Select name="Equipes" placeholder="Pesquise uma equipe" options={teams || []} selected={filters.teams || []} onChange={value => handleChange("teams", value)} multiple={true} />
                    </FormGroup>
                    {permissions?.crm > 1 &&
                        <CollectionFilter label="Responsável" name="Responsáveis" placeholder="Pesquise um responsável" options={getUsersBySelectedTeams(filters.teams, users, teams)} selected={filters.users || []} operator={filters.usersOperator || "any"} onValuesChange={value => handleChange("users", value)} onOperatorChange={value => handleChange("usersOperator", value)} />
                    }

                    <BreakForm title="Aquisição / UTM" showAction={false} borderColor="var(--border-subtle-color)" />
                    <UtmFilters value={filters.utmFilters || []} onChange={value => handleChange("utmFilters", value)} />

                    {additionalFields?.some(field => field.is_visible !== false) &&
                        <>
                            <BreakForm title="Campos adicionais" showAction={false} borderColor="var(--border-subtle-color)" />
                            <AdditionalFieldFilters fields={additionalFields} value={filters.additionalFieldFilters || []} onChange={value => handleChange("additionalFieldFilters", value)} />
                        </>
                    }

                    {(metaAdsAdsName?.length > 0 || metaAdsAdsetsName?.length > 0 || metaAdsCampaignsName?.length > 0 || metaAdsFormsName?.length > 0 || metaAdsPagesName?.length > 0) &&
                        <BreakForm title="Meta Ads" showAction={false} borderColor="var(--border-subtle-color)" />
                    }
                    {metaAdsAdsName?.length > 0 && <FormGroup labelText="Anúncio"><Select name="Ad Name" options={metaAdsAdsName} selected={filters.metaAdsAdsName || []} onChange={value => handleChange("metaAdsAdsName", value)} multiple={true} /></FormGroup>}
                    {metaAdsAdsetsName?.length > 0 && <FormGroup labelText="Conjunto"><Select name="Adset Name" options={metaAdsAdsetsName} selected={filters.metaAdsAdsetsName || []} onChange={value => handleChange("metaAdsAdsetsName", value)} multiple={true} /></FormGroup>}
                    {metaAdsCampaignsName?.length > 0 && <FormGroup labelText="Campanha Meta"><Select name="Campaign Name" options={metaAdsCampaignsName} selected={filters.metaAdsCampaignsName || []} onChange={value => handleChange("metaAdsCampaignsName", value)} multiple={true} /></FormGroup>}
                    {metaAdsFormsName?.length > 0 && <FormGroup labelText="Formulário"><Select name="Form Name" options={metaAdsFormsName} selected={filters.metaAdsFormsName || []} onChange={value => handleChange("metaAdsFormsName", value)} multiple={true} /></FormGroup>}
                    {metaAdsPagesName?.length > 0 && <FormGroup labelText="Página"><Select name="Page Name" options={metaAdsPagesName} selected={filters.metaAdsPagesName || []} onChange={value => handleChange("metaAdsPagesName", value)} multiple={true} /></FormGroup>}
                </div>

                <div className={styles.footer}>
                    <Button variant="secondary" onClick={() => setFilters({})} disabled={loading}>Limpar</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>Solicitar relatório</Button>
                </div>
            </div>
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default ReportDataLead
